export const AUDIT_FIELDS = {
  status: "Status",
  statusActionOwner: "Action pending",
  statusDetail: "Status detail",
  progressModifiedAt: "Progress updated",
  progressModifiedBy: "Progress updated by",
  country: "Country / Region",
  entity: "Entity",
  taxType: "Tax type",
  taxNumber: "Tax number",
  governmentEntity: "Tax authority",
  auditNumber: "Audit number",
  auditStartDate: "Audit period start",
  auditEndDate: "Audit period end",
  auditInitiationDate: "Audit initiation date",
  statuteOfLimitations: "Statute of limitations",
  auditCloseDate: "Audit close date",
  owner: "Owner",
  description: "Description",
  auditName: "Audit name",
  finalAssessments: "Final assessments",
  totalPayments: "Total payments",
  customAttributesDataMap: "Custom attributes",
} as const;

export const TASK_FIELD_MAPPINGS = {
  status: "Status",
  inquiryType: "Type",
  taskGroupType: "Type",
  actualStartDate: "Actual start",
  actualCompletionDate: "Completion date",
  owners: "Owner",
  subInquiryType: "Subtype",
  dueDate: "Task due date",
  complexity: "Complexity",
  taskGroupName: "Task group",
  plannedStartDate: "Planned start",
  dueIn: "Due in",
  priority: "Priority",
  description: "Description",
  ratio: "Progress",
  taskName: "Task name",
  taskType: "Task type",
  isMilestone: "Milestone",
  jurisdictionLevel: "Jurisdiction level",
  reopenReason: "Reopen reason",
  reopenedBy: "Reopened by",
  reopenedAt: "Reopened at",
  statusChangeReason: "Reason",
  parentTaskId: "Parent task",
};

export const GENERAL_FIELD_MAPPINGS = {
  createdAt: "Created at",
  createdBy: "Created by",
  deleted: "Deleted",
  shortId: "Short ID",
};

export const PAYMENT_ASSESSMENT_FIELD_MAPPINGS = {
  dateReceived: "Date received",
  notes: "Notes",
  paymentAuthorizationLink: "Payment authorization link",
  paymentDate: "Payment date",
  paymentRefNumber: "Payment reference number",
  totalInterestAmount: "Total interest amount",
  totalOtherAmount: "Total other amount",
  totalPenaltyAmount: "Total penalty amount",
  totalTaxAmount: "Total tax amount",
  trackingRefNumber: "Tracking reference number",
  type: "Type",
};

export const GAIA_OBJECT_FIELDS = {
  // Direct gaia object fields
  gaiaobject_title: "Name",
  gaiaobject_title_suffix: "Name suffix",
  gaiaobject_category: "Category",
  short_id: "Short ID",
  modified_at: "Modified at",
  created_at: "Created at",

  // Custom attribute fields (these appear in customAttributesDataMap)
  owner: "Owner",
  severity: "Severity",
  country: "Country",
  actualDueDate: "Actual due date",
  statusActionOwner: "Action pending",
  taxPeriodEnd: "Tax period end",
  taxPeriodStart: "Tax period start",
  description: "Description",
  noticeType: "Notice type",
  effort: "Effort",
  statusDetail: "Status detail",
  noticeId: "Notice ID",
  closeDate: "Close date",
  noticeDate: "Notice date",
  entity: "Entity",
  taxType: "Tax type",
  ResponseDeadline: "Response deadline",
  status: "Status",

  // Litigation specific fields
  contestedAmount: "Contested amount",
  litigationTracker: "Litigation tracker",
  initiationDate: "Initiation date",
  referenceNumber: "Reference number",
  litigationType: "Litigation type",
  outsideCounsel: "Outside counsel",
  savings: "Savings",
  taxAuthority: "Tax authority",
  controversyMemo: "Controversy memo",
  publicDecision: "Public decision",
  litigationTrackerId: "Litigation tracker ID",
  liabilityReporting: "Liability reporting",
  statuteOfLimitations: "Statute of limitations",
  closedDate: "Closed date",
  goForward: "Go forward",
  poaRequired: "POA required",
  initialFilingDate: "Initial filing date",
  caseNumber: "Case number",
  filingDate: "Filing date",
  courtName: "Court name",
} as const;

export const ESCALATION_FIELD_MAPPINGS = {
  notify_at: "Notification date",
  recipients: "Recipients",
  notified: "Notified",
  team_name: "Team",
  type: "Type",
} as const;

export const REVIEW_FIELD_MAPPINGS = {
  review_status: "Status",
  reviewer: "Reviewer",
  reviewers: "Reviewers",
  requested_by: "Requested by",
  due_date: "Due date",
  review_date: "Review date",
  comments: "Comments",
  message_for_reviewers: "Message for reviewers",
  deleted: "Deleted",
  review_type: "Review type",
  approvalId: "Approval ID",
  ccUsers: "CC users",
  approved_doc_count: "Approved documents",
  rejected_doc_count: "Rejected documents",
} as const;

export const TEAM_FIELD_MAPPINGS = {
  team_description: "Team description",
  date_format: "Date format",
  ldap_group_id: "LDAP group ID",
  team_custom_attributes_schema: "Team custom attributes schema",
  team_workflows: "Team workflows",
  enabled_features: "Enabled features",
  parent_team_resource_id: "Parent team",
  sub_teams: "Sub-teams",
  field_dependency_rules: "Field dependency rules",
  default_document_types: "Default document types",
  reopen_window_days: "Assigned team reopen window",
} as const;

/**
 * DDB attribute names on the TeamDO whose values are JSON blobs. The split
 * panel pretty-prints these when the value parses as JSON; non-JSON strings
 * (Java `Map.toString()` / `List.toString()` shapes emitted by legacy
 * producers) fall back to raw display via `prettyPrintTeamJson`.
 */
export const TEAM_JSON_BLOB_FIELDS = new Set<string>([
  "team_custom_attributes_schema",
  "team_workflows",
  "field_dependency_rules",
  "default_document_types",
  "sub_teams",
  "enabled_features",
  "escalation_template_levels",
  "review_template_steps",
  "assignment_group_members",
  "assignment_group_shared_emails",
  "assignment_group_criteria",
  "default_rules",
  "custom_requirements",
]);

// Rows from team-config items other than the team record; classified by edge prefix because
// assignment group resource ids are bare short ids.
export const TEAM_CONFIG_EDGE_CATEGORIES = {
  escalation_template_: "Escalation templates",
  review_template_: "Review templates",
  closing_requirements_: "Closing requirements",
  assignment_group_: "User groups",
} as const;

export const TEAM_CONFIG_CATEGORIES = new Set<string>([
  "Team config",
  ...Object.values(TEAM_CONFIG_EDGE_CATEGORIES),
]);

export const TEAM_CONFIG_ITEM_FIELD_MAPPINGS = {
  escalation_template_name: "Template name",
  escalation_template_levels: "Escalation levels",
  review_template_name: "Template name",
  review_template_type: "Review type",
  review_template_steps: "Review steps",
  assignment_group_name: "Group name",
  assignment_group_type: "Group type",
  assignment_group_members: "Members",
  assignment_group_shared_emails: "Shared emails",
  assignment_group_criteria: "Criteria",
  assignment_group_default: "Default group",
  default_rules: "Default rules",
  custom_requirements: "Custom requirements",
  deleted: "Deleted",
  created_by: "Created by",
  created_at: "Created at",
} as const;

export type AuditFieldKey = keyof typeof AUDIT_FIELDS;
export type TaskFieldKey = keyof typeof TASK_FIELD_MAPPINGS;
export type GeneralFieldKey = keyof typeof GENERAL_FIELD_MAPPINGS;
export type PaymentAssessmentFieldKey =
  keyof typeof PAYMENT_ASSESSMENT_FIELD_MAPPINGS;
export type GaiaObjectFieldKey = keyof typeof GAIA_OBJECT_FIELDS;
export type EscalationFieldKey = keyof typeof ESCALATION_FIELD_MAPPINGS;
export type ReviewFieldKey = keyof typeof REVIEW_FIELD_MAPPINGS;
export type TeamFieldKey = keyof typeof TEAM_FIELD_MAPPINGS;
export type TeamConfigItemFieldKey =
  keyof typeof TEAM_CONFIG_ITEM_FIELD_MAPPINGS;
